// Cálculo de ocupación a partir de los eventos del calendario.
//
// Reglas:
// - Un evento ocupa TODOS los días de su rango startTime→endTime (inclusive),
//   no solo el día de inicio.
// - Tolerante a datos raros: sin endTime válido o endTime <= startTime →
//   ocupa solo el día de inicio; un endTime a las 00:00 exactas no ocupa el
//   día siguiente; spans absurdos se capan a MAX_SPAN_DAYS.
// - Filtro "finde": la unidad es el FIN DE SEMANA completo (vie–dom, anclado
//   en su sábado) — ocupado si cualquiera de sus tres días tiene evento.
//   Así "3 de 5 fines de semana" da 60%, que es como piensa un venue, y no
//   3 de 13 días sueltos.

const MAX_SPAN_DAYS = 31;

export const toJsDate = (d) => {
  if (!d) return null;
  if (typeof d.toDate === "function") return d.toDate();
  if (typeof d.seconds === "number") return new Date(d.seconds * 1000);
  if (typeof d === "string" || typeof d === "number") return new Date(d);
  return null;
};

const pad = (n) => String(n).padStart(2, "0");
export const dayKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// Días (keys) que ocupa un evento, expandiendo su rango.
export function eventDayKeys(event) {
  const start = toJsDate(event.startTime);
  if (!(start instanceof Date) || isNaN(start)) return [];
  const keys = [dayKey(start)];
  const end = toJsDate(event.endTime);
  if (end instanceof Date && !isNaN(end) && end > start) {
    let endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    // Termina a medianoche exacta → el último día ocupado es el anterior
    if (end.getHours() === 0 && end.getMinutes() === 0) {
      endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate() - 1);
    }
    const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    for (let i = 1; i <= MAX_SPAN_DAYS; i++) {
      const d = new Date(startDay.getFullYear(), startDay.getMonth(), startDay.getDate() + i);
      if (d > endDay) break;
      keys.push(dayKey(d));
    }
  }
  return keys;
}

// Set de días ocupados ("YYYY-MM-DD") a partir de la lista cruda de eventos.
export function buildOccupiedSet(events) {
  const set = new Set();
  (events || [])
    .filter((e) => e.eventType === "event" && e.status !== "cancelado")
    .forEach((e) => eventDayKeys(e).forEach((k) => set.add(k)));
  return set;
}

const isSemana = (dow) => dow >= 1 && dow <= 4;

// Ocupación de un rango [start, endExclusive) según el filtro.
// Devuelve { ocupados, total, unidad } donde unidad ∈ "días" | "fines de semana".
export function rangoOcupacion(set, start, endExclusive, filtro) {
  let ocupados = 0;
  let total = 0;
  if (filtro === "finde") {
    // Unidad = fin de semana anclado en su sábado
    for (let d = new Date(start); d < endExclusive; d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)) {
      if (d.getDay() !== 6) continue;
      total++;
      const vie = new Date(d.getFullYear(), d.getMonth(), d.getDate() - 1);
      const dom = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
      if (set.has(dayKey(vie)) || set.has(dayKey(d)) || set.has(dayKey(dom))) ocupados++;
    }
    return { ocupados, total, unidad: "fines de semana" };
  }
  const match = filtro === "semana" ? isSemana : () => true;
  for (let d = new Date(start); d < endExclusive; d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)) {
    if (!match(d.getDay())) continue;
    total++;
    if (set.has(dayKey(d))) ocupados++;
  }
  return { ocupados, total, unidad: "días" };
}

export const mesOcupacion = (set, year, month, filtro) =>
  rangoOcupacion(set, new Date(year, month, 1), new Date(year, month + 1, 1), filtro);

export const anioOcupacion = (set, year, filtro) =>
  rangoOcupacion(set, new Date(year, 0, 1), new Date(year + 1, 0, 1), filtro);

// Años presentes en el set (para el desglose "Por año")
export function yearsInSet(set) {
  const years = new Set();
  set.forEach((k) => years.add(Number(k.slice(0, 4))));
  return Array.from(years).sort((a, b) => b - a);
}

// % de ocupación por día de la semana para una lista de años.
// Devuelve filas Lun..Dom: { dia, [año]: pct }.
export function ocupacionPorDiaSemana(set, years) {
  const DOW_LABELS = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
  const DOW_ORDER = [1, 2, 3, 4, 5, 6, 0];
  const totals = new Map();
  const occupied = new Map();
  years.forEach((y) => {
    for (let d = new Date(y, 0, 1); d.getFullYear() === y; d = new Date(y, d.getMonth(), d.getDate() + 1)) {
      const k = `${y}-${d.getDay()}`;
      totals.set(k, (totals.get(k) || 0) + 1);
      if (set.has(dayKey(d))) occupied.set(k, (occupied.get(k) || 0) + 1);
    }
  });
  return DOW_ORDER.map((dow) => {
    const row = { dia: DOW_LABELS[dow] };
    years.forEach((y) => {
      const t = totals.get(`${y}-${dow}`) || 1;
      row[y] = Math.round(((occupied.get(`${y}-${dow}`) || 0) / t) * 100);
    });
    return row;
  });
}
